import type { ButtonHTMLAttributes, Ref } from 'react';
import Icon from '../Icon';
import { Spinner } from '../Spinner';
import { cn } from '../cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-accent-gradient text-on-accent',
  secondary: 'bg-secondary text-text',
  ghost: 'bg-transparent text-accent hover:bg-surface-high',
  danger: 'bg-error-container text-on-error-container',
  link: 'h-auto bg-transparent p-0 text-accent underline-offset-2 hover:underline',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-xs',
  md: 'h-12 px-6 text-sm',
  lg: 'h-14 px-8 text-base font-bold',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  leadingIcon?: string;
  trailingIcon?: string;
  ref?: Ref<HTMLButtonElement>;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leadingIcon,
  trailingIcon,
  className,
  disabled,
  type = 'button',
  children,
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full border-none font-semibold cursor-pointer transition-all active:scale-[0.98]',
        'disabled:cursor-not-allowed disabled:bg-disabled disabled:text-on-disabled disabled:opacity-70 disabled:active:scale-100',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? (
        <Spinner size="sm" />
      ) : leadingIcon ? (
        <Icon name={leadingIcon} className="text-xl" />
      ) : null}
      {children}
      {trailingIcon ? <Icon name={trailingIcon} className="text-xl" /> : null}
    </button>
  );
}
